import { Component, OnInit, ViewChild } from '@angular/core';
import { ImportsModule } from '../../imports';
import { ChartService } from '../../services/chart.service';
import { ChartData } from '../ChartData';
import { UIChart } from 'primeng/chart';
import { Column } from '../Column';
import { DetailTableData } from '../../shared/types';
import { DetailTableComponent } from "../../shared/components/detail-table/detail-table.component";
import { formatNumber } from '@angular/common';

@Component({
  selector: 'app-distribution',
  standalone: true,
  imports: [
    ImportsModule,
    DetailTableComponent
],
  templateUrl: './distribution.component.html',
  styleUrl: './distribution.component.css'
})
export class DistributionComponent implements OnInit {
  @ViewChild('chart') chart!: UIChart;
  chartResults: ChartData = {
    labels: [],
    datasets: []
  }

  tableResults :{
    [key: string]: string,
    resolved_scope: string,
    scopephase: string,
    object_library: string,
    object_name: string,
    object_type: string,
    object_attribute: string,
    object_text: string,
    object_srclines: string
  }[] = [];
  filteredDetailTableResults: any[] = [];
  chartOptions: any;
  columns: Column[] = [];
  selectedValues: { [key: string]: any[] } = {};
  selectedMatchModes: { [key: string]: string } = {};
  globalFilterFields: string[] = [];
  detailTableData: DetailTableData = {} as DetailTableData;

  phaseOptions: any[] = [];
  phaseValue: any[] = [];
  chartTitle = 0;
  
  constructor(
    private chartService: ChartService
  ) { }
  
  ngOnInit() {

    this.chartService.getDistributionPhases().subscribe({
      next: (data: any) => {
        this.phaseOptions = data.rows.map((item: any) => ({ label: item.scopedesc || 'None', value: item.scopephase }));
        this.phaseValue = this.phaseOptions.map((item: any) => item.value);
      }
    });

    this.columns = [
      { label: "Resolved Scope", field: "resolved_scope", width: 200, filterType: 'select' },
      { label: 'Scope Phase', field: 'scopedesc', width: 100, filterType: 'select' },  
      { label: 'Library', field: 'object_library', width: 175, filterType: 'text', matchMode: "contains"  },
      { label: 'Name', field: 'object_name', width: 175, filterType: 'text', matchMode: "contains"  },
      { label: 'Type', field: 'object_type', width: 100, filterType: 'select'},
      { label: 'Attribute', field: 'object_attribute', width: 150, filterType: 'select' },
      { label: 'Text', field: 'object_text', width: 200, filterType: 'text', matchMode: "contains"  },
      { label: 'srclines', field: 'object_srclines', width: 100, filterType: 'numeric' },
    ]

    this.detailTableData = {
      columns: this.columns,
      globalFilterFields: this.globalFilterFields,
      items: this.tableResults,
      detailTableResults: this.tableResults
    }

    const scalesOptions = {
      y: {
        ticks: {
          display: false
        },
        grid: {
          display: false
        }
      },
      x: {
        title: {
          display: true,
          align: 'start',
          text: 'Source Size'
        }
      }
    }
    this.chartOptions = {
      scales: scalesOptions,
      maxBarThickness: 50,
      plugins: {
        title: {
          display: true,
          align: 'center',
          color: '#186182',
          font: {
            size: 18
          },
          text:  `${this.chartTitle} In-Scope Program Source`
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'end',
          anchor: 'end',
          clamp: true,
          formatter: function(value: any) {
            return formatNumber(value, 'en-US', '1.0-0');
          }
        }
      }
    }

    this.populateChart();
  }

  getColumnOptions(field: string): any[] {
    const uniqueValues = Array.from(new Set(this.tableResults.map((item) => item[field])));
    return uniqueValues.map(value => ({ label: value, value }));
  }

  filterBySelectedValues() {
    this.filteredDetailTableResults = this.tableResults.filter(item => {
      return Object.keys(this.selectedValues).every(field => {
        const selectedValues: any = this.selectedValues[field];
        if (selectedValues && selectedValues.length) {
          if (Array.isArray(selectedValues)) {
            // Handle select filters
            return selectedValues.includes(item[field]);
          } else {
            const matchMode = this.selectedMatchModes[field] || 'contains';
            // Handle text filters with match modes
            const value = item[field] ? item[field].toString().toLowerCase() : '';
            const filterValue = selectedValues.toString().toLowerCase();
            switch (matchMode) {
              case 'contains':
                return value.includes(filterValue);
              case 'equals':
                return value === filterValue;
              case 'startsWith':
                return value.startsWith(filterValue);
              case 'endsWith':
                return value.endsWith(filterValue);
              default:
                return true;
            }
          }
        }
        return true;
      });
    });
  }

  resetChart(){
    this.chartResults = {
      labels: [],
      datasets: []
    }
  }

  populateChart(phase: string = ''){
    this.chartService.getDistributionChart(phase).subscribe({
      next: (chartData: any) => {
        this.resetChart();
        if(chartData.rowsAffected > 0){
          const data = chartData.rows.map((item: any) => {
            this.chartTitle += item.totalobj;
            this.chartResults.labels.push(item.srcsize);
            return item.totalobj;
          });

          this.chartResults.datasets.push({
            label: "Total Objects",
            data: data,
            backgroundColor: '#0E9ED5',
            borderColor: '#0E9ED5'
          });
        }        
      },
      complete: () => {
        this.chartOptions.plugins.title.text = `${formatNumber(this.chartTitle, 'en-US', '1.0-0')} In-Scope Program Source`;
        this.chart.reinit();
      }
    });
  }

  filterByRange(event: any){
    const selectedRange = this.chartResults.labels[event.element.index];
    this.chartService.getDistributionDetail(selectedRange).subscribe({
      next: (data: any) => {
        this.tableResults = data.rows;
        this.filteredDetailTableResults = this.tableResults;
        this.detailTableData = {
          items: this.tableResults,
          columns: this.columns,
          globalFilterFields: this.globalFilterFields,
          detailTableResults: this.tableResults
        }
      }
    });
  }


  onScopePhaseChange(event: any, type: string) {
    if (type === 'scope') {
      // this.scopeValue = event.value;
    } else {
      this.phaseValue = event.value;
    }
    const phaseValues = this.phaseValue.map((value: any) => value === null ? 'null' : value);
    this.populateChart(phaseValues.join(','));
  }

}
